import { FeedbackService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ReportFeedbackData } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Button,Flashbar, Form, FormField, Input, Modal, Textarea } from '@amzn/awsui-components-react';
import React, {useState} from 'react';
import { useIsMounted } from 'react-tidy';

import { constants,feedbackWindowConstants } from '../../../common/Localization';
import { getAppId } from '../../../common/util';

interface IFlashMessage {
   message: string,
   type: any
}

type Props = FlashContextInterface & {
    feedbackType: FeedbackType;
    closeModal: () => void,
    userEmail: string;
    isAwsUser: boolean;
}

export enum FeedbackType {
    BUG = 'bug',
    FEATURE = 'feature'
}

const feedbackService = new FeedbackService();

function BugAndFeatureRequest({
    feedbackType, userEmail, isAwsUser, closeModal, flash}: Props): JSX.Element | null {
    
    const isMounted = useIsMounted();
    const getDescription = (feedbackType: string): string => {

        return feedbackType === FeedbackType.BUG ? feedbackWindowConstants.BUG_DESCRIPTION :
            feedbackWindowConstants.FEATURE_DESCRIPTION;
    };
    
    const getTitle = (feedbackType: string): string => {

        return feedbackType === FeedbackType.BUG ? feedbackWindowConstants.BUG_TITLE : feedbackWindowConstants.FEATURE_TITLE;
    };

    const [ description, setDescription ] = useState(getDescription(feedbackType));
    const [ errorTitleMsg, setTitleErrorMsg ] = useState('');
    const [ errorDescriptionMsg, setDescriptionErrorMsg ] = useState('');
    const flashValue : IFlashMessage = { message: feedbackWindowConstants.FLASH_INFO, type: 'info' };
    const [ flashMessage, setFlashMessage] = useState(flashValue);
    const  header = feedbackType === FeedbackType.BUG ? feedbackWindowConstants.BUG_HEADER : feedbackWindowConstants.FEATURE_HEADER;
    const [ loading, setLoading ] = useState(false);
    const [ showFlashBar, setShowFlashBar ] = useState(false);
    const [ title, setTitle ] = useState(getTitle(feedbackType));
   
    const updateDescription = (updatedDescription: string) => {
        setDescription(updatedDescription);
        setDescriptionErrorMsg('');
    };

    const updateTitle = (updatedTitle: string) => {
        setTitle(updatedTitle);
        setTitleErrorMsg('');
    };

    const updateFlashMessage = (message: string, type: string) => {
        flashValue.message = message;
        flashValue.type = type;
        setFlashMessage(flashValue);
    };
    
    const validateForm = (): boolean => {
        return (validateTitle() && validateDescription());
    };

    const validateTitle = (): boolean => {

        if (title === '') {
            setTitleErrorMsg(feedbackWindowConstants.REQUIRED_FIELD);
            return false;
        } else {
            setTitleErrorMsg('');
            return true;
        }
    };
    
    const validateDescription = (): boolean => {

        if (description === '') {
            setDescriptionErrorMsg(feedbackWindowConstants.REQUIRED_FIELD);
            return false;
        } else {
            setDescriptionErrorMsg('');
            return true;
        }
    };

    const getuserId = (userEmail: string): string => {

        return userEmail.split('@')[0];
    };
    
    const getRequestPayload = (): ReportFeedbackData => {

        const reportFeedbackData: ReportFeedbackData = {
            title: title,
            description: description,
            type: feedbackType === FeedbackType.BUG ? FeedbackType.BUG : FeedbackType.FEATURE,
            appId: getAppId(),
            userEmail: userEmail,
            requester: isAwsUser ? getuserId(userEmail) : ''
        };
        return reportFeedbackData;
    };
    
    const onSubmitIssueClick = async () => {
        if (!validateForm()) {
            return;
        }
         
        setLoading(true);
        setShowFlashBar(true);
        
        try {
            await feedbackService.reportFeedback(getRequestPayload());
            if (isMounted()) {
                flash.alert(FlashType.success,feedbackWindowConstants.SUCCESS_MESSAGE,feedbackWindowConstants.FLASH_SUCCESS_TITLE);
                closeModal();
            }
        } catch (err) {
            if (isMounted()) {
                setShowFlashBar(true);
                updateFlashMessage(feedbackWindowConstants.FLASH_ERROR+feedbackWindowConstants.CONTACT_EMAIL,FlashType.error);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    const formActions = (
        <div>
            <Button variant='link' data-testid='btn-cancel' onClick={closeModal}>{feedbackWindowConstants.CANCEL}</Button>
            <Button variant='primary' data-testid='btn-submit' onClick={onSubmitIssueClick} loading={loading} >{feedbackWindowConstants.SUBMIT}</Button>
        </div>
    );

    return (
        <div className='form-action'>
            <Modal header={header} onDismiss={closeModal} visible>
                <Form actions={formActions} >
                    <section className='awsui-util-mb-m'>
                        <FormField label={feedbackWindowConstants.TITLE} errorText={errorTitleMsg}>
                            <Input data-testid='input-title' value={title} onChange={ event => updateTitle(event.detail.value)}/>
                        </FormField>
                        <FormField  label={constants.DESCRIPTION} errorText={errorDescriptionMsg} >
                            <Textarea
                                value={description}
                                onChange={ event => updateDescription(event.detail.value)}
                                rows={5}
                                data-testid='textarea-description'/>
                        </FormField>
                        {showFlashBar?
                            (
                                <Flashbar  
                                    items={[ 
                                        {
                                            content:flashMessage.message, 
                                            type: flashMessage.type,
                                            dismissible: true,
                                            onDismiss: () => setShowFlashBar(false)
                                        }
                                    ]} 
                                />
                            )
                            :null
                        }
                        
                    </section>
                </Form>
            </Modal>
        </div>
    );
}

export default withFlashContext(BugAndFeatureRequest);
